import { CommonModule } from "@angular/common";
import { Component, DestroyRef, computed, inject, input, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { distinctUntilChanged, map } from "rxjs/operators";

import { formatRecordLabel } from "@app/case-context/components/case-context-record-grid/case-context-record-grid-columns";
import { WorkflowLedgerComponent } from "@app/shared/components/workflow-ledger/workflow-ledger.component";
import { WorkflowLedger, WorkflowLedgerViewState } from "@app/shared/components/workflow-ledger/workflow-ledger.models";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { formatPersonNameLastFirst } from "@app/shared/util/person-name";

import { PhmsAuditActivityLogComponent } from "../audit-activity/phms-audit-activity-log.component";
import { HearingRecordDto, HearingRecordIncarcerationTerm } from "../hearing-record/hearing-record.models";
import { HearingPhase } from "../navigation/phms-detail-navigation";
import { PHMS_HEARING_TERMINOLOGY } from "../phms-hearing.terminology";

/** Query parameter that opens the header's details region on load (`?details=open`, #1530). */
export const HEARING_DETAILS_QUERY_PARAM = "details";
export const HEARING_DETAILS_OPEN_VALUE = "open";

export type HearingStatusTone = "neutral" | "active" | "success" | "danger";

/**
 * Page header for one routed hearing phase (#1530). Shows the phase title, the proceeding's
 * scheduling status and the runtime's current step in place of the app-wide workflow summary bar,
 * and a "Details & history" disclosure that expands, in place, the identity essentials and hearing
 * facts the Case Snapshot rail used to carry plus the workflow history and the hearing's audit
 * activity.
 */
@Component({
  selector: "phms-hearing-page-header",
  standalone: true,
  imports: [CommonModule, RouterLink, WorkflowLedgerComponent, PhmsAuditActivityLogComponent],
  templateUrl: "./hearing-page-header.component.html",
  styleUrl: "./hearing-page-header.component.scss",
})
export class HearingPageHeaderComponent {
  private static nextInstance = 0;
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly hearingText = createTerminologyUsageSignals(PHMS_HEARING_TERMINOLOGY.hearingPhasePage);

  readonly phase = input.required<HearingPhase>();
  /** Scheduling status text of the proceeding (e.g. Scheduled, Completed). */
  readonly status = input<string | null>(null);
  readonly statusCode = input<string | null>(null);
  /** Current runtime step / hearing subphase the shell derives. */
  readonly currentStepLabel = input<string | null>(null);
  readonly record = input<HearingRecordDto | null>(null);
  readonly scheduledProceedingId = input<number | null>(null);
  readonly ledger = input<WorkflowLedger | null>(null);
  readonly ledgerState = input<WorkflowLedgerViewState>("loading");
  readonly isLoading = input(false);
  readonly launchHearingId = computed(() => {
    const recordId = this.record()?.scheduledProceedingId;
    return !this.isLoading() && Number.isSafeInteger(recordId) && (recordId ?? 0) > 0
      && recordId === this.scheduledProceedingId() ? recordId : null;
  });

  readonly detailsId = `hearing-page-details-${++HearingPageHeaderComponent.nextInstance}`;
  readonly expanded = signal(this.route.snapshot?.queryParams?.[HEARING_DETAILS_QUERY_PARAM] === HEARING_DETAILS_OPEN_VALUE);

  readonly title = computed(() => {
    switch (this.phase()) {
      case "at":
        return this.hearingText.phaseAtTitle();
      case "post":
        return this.hearingText.phasePostTitle();
      default:
        return this.hearingText.phasePreTitle();
    }
  });
  readonly statusTone = computed<HearingStatusTone>(() => statusTone(this.statusCode() ?? this.status()));
  readonly demographics = computed(() => this.record()?.context?.demographics ?? null);
  readonly proceeding = computed(() => this.record()?.context?.proceeding ?? null);
  readonly custodyTerm = computed<HearingRecordIncarcerationTerm | null>(() => {
    const terms = this.record()?.context?.incarcerationHistory?.terms ?? [];
    return terms.find((term) => term.isCurrent) ?? terms[0] ?? null;
  });
  readonly subjectName = computed(() => {
    const demographics = this.demographics();
    const full = demographics?.fullName?.trim()
      || [demographics?.firstName, demographics?.lastName].filter(Boolean).join(" ");
    return formatPersonNameLastFirst(full) || full;
  });
  readonly decisionDetail = computed(() => {
    const outcome = this.record()?.decision?.outcome;
    const finding = outcome?.finding?.trim();
    return finding ? formatRecordLabel(finding) : "";
  });
  readonly decisionRecordedUtc = computed(() => this.record()?.decision?.outcome?.recordedUtc ?? null);
  readonly panelLabel = computed(() => {
    const panel = this.proceeding()?.panel ?? [];
    if (panel.length === 0) return "Panel not assigned";
    return panel.map((member) => `${member.name ?? "Unnamed"}${member.isPresiding ? " (Presiding)" : ""}`).join(", ");
  });
  readonly locationLabel = computed(() => {
    const proceeding = this.proceeding();
    return [proceeding?.locationName, proceeding?.roomName]
      .filter((part) => !!part && part.trim().length > 0)
      .join(" - ") || "--";
  });
  readonly requirementsLabel = computed(() => {
    const proceeding = this.proceeding();
    if (proceeding?.requirementsTotal == null) return "--";
    return `${proceeding.requirementsComplete ?? 0}/${proceeding.requirementsTotal} complete`;
  });
  readonly requirements = computed(() => this.proceeding()?.requirements ?? []);
  readonly ageDobLabel = computed(() => {
    const demographics = this.demographics();
    const age = demographics?.age == null ? "" : String(demographics.age);
    const dob = demographics?.dateOfBirth ? this.formatDate(demographics.dateOfBirth) : "";
    return [age, dob].filter((part) => part.length > 0).join(" / ") || "--";
  });

  constructor() {
    this.route.queryParams
      ?.pipe(
        map((params) => params[HEARING_DETAILS_QUERY_PARAM] === HEARING_DETAILS_OPEN_VALUE),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((open) => this.expanded.set(open));
  }

  toggle(): void {
    const next = !this.expanded();
    this.expanded.set(next);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [HEARING_DETAILS_QUERY_PARAM]: next ? HEARING_DETAILS_OPEN_VALUE : null },
      queryParamsHandling: "merge",
      preserveFragment: true,
      replaceUrl: true,
    });
  }

  display(value: string | number | null | undefined): string {
    return value == null || String(value).trim() === "" ? "--" : String(value);
  }

  formatDate(value: string | null | undefined): string {
    const dateOnly = (value ?? "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
      return `${Number(dateOnly[2])}/${Number(dateOnly[3])}/${dateOnly[1]}`;
    }
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) {
      return "--";
    }
    return new Intl.DateTimeFormat(undefined, { month: "numeric", day: "numeric", year: "numeric" }).format(date);
  }

  formatDateTime(value: string | null | undefined): string {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) {
      return "--";
    }
    return new Intl.DateTimeFormat(undefined, {
      month: "numeric", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    }).format(date);
  }

  isRequirementComplete(statusCode: string | null | undefined): boolean {
    return (statusCode ?? "").trim().toLowerCase() === "complete";
  }
}

function statusTone(value: string | null): HearingStatusTone {
  const code = (value ?? "").trim().toLowerCase().replace(/[_-]+/g, " ");
  if (!code) return "neutral";
  if (["completed", "complete", "final", "decision recorded"].includes(code)) return "success";
  if (["cancelled", "canceled", "vacated", "unscheduled"].includes(code)) return "danger";
  if (["scheduled", "in progress", "started", "confirmed"].includes(code)) return "active";
  return "neutral";
}
